import React from "react";
import { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { toast } from "react-toastify";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { accountConstants, userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { cancelRollover } from "actions/userActions";
import { setAccountSession } from "actions/accountActions";
import { auth0AccountSessionSelector } from "store/selectors/auth0";
import { get, find } from "lodash";
import { Modal } from "react-bootstrap";
import { getAllAccounts } from "actions/accountActions";
import {
  newActiveRolloverAccount,
  activeRolloverAccountSameAsCurrent,
  activeRolloverIsMixed,
} from "store/selectors/user";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";

import "./RolloverInstructionsModal.scss";

const actions = [userConstants.CANCEL_ROLLOVER, accountConstants.GET_ACCOUNTS];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const noneRolloverAccountId = find(accounts, { isRollover: false })?.id;
  const firstAccountId = get(accounts, "[0].id", null);

  let accountId: string | null | undefined = null;

  if (newActiveRolloverAccount(state)) {
    accountId = noneRolloverAccountId;
  } else if (activeRolloverAccountSameAsCurrent(state)) {
    accountId = auth0AccountSessionSelector(state);
  } else if (activeRolloverIsMixed(state)) {
    accountId = firstAccountId;
  }

  return {
    accountId,
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
    activeAccountId: auth0AccountSessionSelector(state),
    isRolloverAccountNew: newActiveRolloverAccount(state),
    isRolloverSameAsCurrent: activeRolloverAccountSameAsCurrent(state),
    isRolloverIsMixed: activeRolloverIsMixed(state),
  };
};

const mapDispatchToProps = {
  push,
  cancelRollover,
  getAllAccounts,
  setAccountSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  show?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  selectedAccount: string;
  showDeletedComponent: boolean;
  submitted: boolean;
}

class CancelRolloverModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedAccount: "",
      showDeletedComponent: false,
      submitted: false,
    };
  }

  _cancelRolloverAccount = () => {
    void this.props
      .cancelRollover(
        this.props.client as ApolloClient<object>,
        this.props.activeAccountId as string
      )
      .then(async () => {
        if (!this.props.error) {
          await this.props.getAllAccounts(
            this.props.client as ApolloClient<object>
          );
          await this.props.setAccountSession(
            this.props.client as ApolloClient<object>,
            this.props.accountId as string
          );
          toast.success("You have successfully canceled your rollover.");

          window.location.search = `?redirect_to=/dashboard`;
          window.analytics.track("Cancelled Individual Rollover");

          this.setState({
            submitted: true,
          });
        } else {
          console.error("Error found:", this.props.error);
        }
      })
      .catch((error) => {
        toast.error("There was a problem canceling your rollover.");
        console.error("An error occurred:", error);
      });
  };

  render() {
    if (this.props.show) {
      return (
        <Modal
          centered
          scrollable
          show={this.props.show}
          className="rollover-modal"
        >
          <Modal.Header className="modal-header">
            <IconHeader
              variant="cardHeader"
              headerText="Are you sure you want to cancel your rollover?"
            />
            <IconSubheader subheader="This will terminate your active rollover." />
          </Modal.Header>

          <Modal.Footer>
            <Button
              color="dismiss"
              btnLabel="Close"
              type="button"
              name="cancel"
              onClick={this.props.onClose}
            />
            <Button
              color="red"
              name="cancel"
              withArrow={true}
              loading={this.state.submitted}
              btnLabel="Cancel Rollover"
              onClick={() => this._cancelRolloverAccount()}
            />
          </Modal.Footer>
        </Modal>
      );
    }
    return null;
  }
}

export default connector(
  withApollo<OwnProps & PropsFromRedux>(CancelRolloverModal)
);
